import * as React from 'react';

interface TOCItem {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: 'blue' | 'green' | 'purple' | 'orange' | 'red' | 'gray' | 'yellow' | 'indigo' | 'pink' | 'teal';
}

interface TableOfContentsGridProps {
  items: TOCItem[];
  title?: string;
}

const colorClasses = {
  blue: {
    bg: 'bg-white',
    border: 'border-gray-300',
    hover: 'hover:bg-gray-50',
    text: 'text-black',
    link: 'text-gray-700 hover:text-black'
  },
  green: {
    bg: 'bg-white',
    border: 'border-gray-300',
    hover: 'hover:bg-gray-50',
    text: 'text-black',
    link: 'text-gray-700 hover:text-black'
  },
  purple: {
    bg: 'bg-white',
    border: 'border-gray-300',
    hover: 'hover:bg-gray-50',
    text: 'text-black',
    link: 'text-gray-700 hover:text-black'
  },
  orange: {
    bg: 'bg-white',
    border: 'border-gray-300',
    hover: 'hover:bg-gray-50',
    text: 'text-black',
    link: 'text-gray-700 hover:text-black'
  },
  red: {
    bg: 'bg-white',
    border: 'border-gray-300',
    hover: 'hover:bg-gray-50',
    text: 'text-black',
    link: 'text-gray-700 hover:text-black'
  },
  gray: {
    bg: 'bg-white',
    border: 'border-gray-300',
    hover: 'hover:bg-gray-50',
    text: 'text-black',
    link: 'text-gray-700 hover:text-black'
  },
  yellow: {
    bg: 'bg-white',
    border: 'border-gray-300',
    hover: 'hover:bg-gray-50',
    text: 'text-black',
    link: 'text-gray-700 hover:text-black'
  },
  indigo: {
    bg: 'bg-white',
    border: 'border-gray-300',
    hover: 'hover:bg-gray-50',
    text: 'text-black',
    link: 'text-gray-700 hover:text-black'
  },
  pink: {
    bg: 'bg-white',
    border: 'border-gray-300',
    hover: 'hover:bg-gray-50',
    text: 'text-black',
    link: 'text-gray-700 hover:text-black'
  },
  teal: {
    bg: 'bg-white',
    border: 'border-gray-300',
    hover: 'hover:bg-gray-50',
    text: 'text-black',
    link: 'text-gray-700 hover:text-black'
  }
};

export function TableOfContentsGrid({ items, title = "Table of Contents" }: TableOfContentsGridProps) {
  const handleCardClick = (itemId: string) => {
    const element = document.getElementById(itemId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="my-8">

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((item) => {
          const colors = colorClasses[item.color];

          return (
            <div
              key={item.id}
              className={`${colors.bg} ${colors.border} ${colors.hover} border rounded-lg p-4 transition-colors cursor-pointer`}
              onClick={() => handleCardClick(item.id)}
            >
              <h3 className={`${colors.text} font-semibold mb-2`}>
                {item.icon} {item.title}
              </h3>
              <a
                href={`#${item.id}`}
                className={`${colors.link} font-medium block mb-2`}
                onClick={(e) => e.stopPropagation()}
              >
                {item.title}
              </a>
              <p className="text-sm text-gray-600">
                {item.description}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Predefined configurations for common documentation patterns
export const loginGuideTOC: TOCItem[] = [
  {
    id: 'accessing-the-platform',
    title: 'Getting Started',
    description: 'Learn how to navigate to and access the platform',
    icon: '🚀',
    color: 'blue'
  },
  {
    id: 'logging-in-with-email-and-password',
    title: 'Email Login',
    description: 'Step-by-step guide for email/password authentication',
    icon: '🔐',
    color: 'green'
  },
  {
    id: 'logging-in-with-google-optional',
    title: 'Google Login',
    description: 'Quick authentication using Google OAuth',
    icon: '🔗',
    color: 'purple'
  },
  {
    id: 'email-verification',
    title: 'Email Verification',
    description: 'Complete your account verification process',
    icon: '📧',
    color: 'orange'
  },
  {
    id: 'password-reset',
    title: 'Password Reset',
    description: 'Reset your password if forgotten',
    icon: '🔑',
    color: 'red'
  },
  {
    id: 'troubleshooting-login-issues',
    title: 'Troubleshooting',
    description: 'Solutions for common login problems',
    icon: '🛠️',
    color: 'gray'
  }
];

export const mediaGuideTOC: TOCItem[] = [
  {
    id: 'accessing-the-media-library',
    title: 'Media Library',
    description: 'Navigate and understand the media library interface',
    icon: '📚',
    color: 'blue'
  },
  {
    id: 'uploading-images',
    title: 'Upload Images',
    description: 'Add photos and graphics to your media library',
    icon: '🖼️',
    color: 'green'
  },
  {
    id: 'uploading-videos',
    title: 'Upload Videos',
    description: 'Add video files and presentations',
    icon: '🎥',
    color: 'purple'
  },
  {
    id: 'creating-apps',
    title: 'Create Apps',
    description: 'Build interactive content with various app types',
    icon: '📱',
    color: 'orange'
  },
  {
    id: 'using-playlists',
    title: 'Playlists',
    description: 'Organize media into sequential playlists',
    icon: '🎵',
    color: 'red'
  }
];

export const screensGuideTOC: TOCItem[] = [
  {
    id: 'understanding-screens',
    title: 'Understanding Screens',
    description: 'Learn what screens are and how they work',
    icon: '📺',
    color: 'blue'
  },
  {
    id: 'managing-screens',
    title: 'Manage Screens',
    description: 'View, edit, and organize your display devices',
    icon: '⚙️',
    color: 'orange'
  },
  {
    id: 'troubleshooting-screens',
    title: 'Troubleshooting',
    description: 'Solve common screen connection and display issues',
    icon: '🛠️',
    color: 'red'
  }
];

export const publicationsGuideTOC: TOCItem[] = [
  {
    id: 'understanding-publications',
    title: 'Understanding Publications',
    description: 'Learn what publications are and how they work',
    icon: '📰',
    color: 'blue'
  },
  {
    id: 'accessing-publications',
    title: 'Accessing Publications',
    description: 'Navigate to the publications management interface',
    icon: '🔍',
    color: 'green'
  },
  {
    id: 'creating-a-publication',
    title: 'Creating a New Publication',
    description: 'Follow the video tutorial to create your first publication',
    icon: '✨',
    color: 'purple'
  }
];

export const userGuideTOC: TOCItem[] = [
  {
    id: '1-getting-started---login',
    title: 'Getting Started',
    description: 'Learn how to access and authenticate with the platform',
    icon: '🚀',
    color: 'blue'
  },
  {
    id: '2-managing-media',
    title: 'Managing Media',
    description: 'Upload, organize, and create content for your displays',
    icon: '📚',
    color: 'green'
  },
  {
    id: '3-creating-screens',
    title: 'Creating Screens',
    description: 'Set up and manage your display devices',
    icon: '📺',
    color: 'purple'
  },
  {
    id: '4-creating-publications',
    title: 'Creating Publications',
    description: 'Build content layouts and assign them to screens',
    icon: '📰',
    color: 'orange'
  },
  {
    id: '5-frequently-asked-questions',
    title: 'FAQ',
    description: 'Common questions and troubleshooting solutions',
    icon: '❓',
    color: 'red'
  }
];

// Individual components for each guide
export function LoginGuideTOC() {
  return <TableOfContentsGrid items={loginGuideTOC} />;
}

export function MediaGuideTOC() {
  return <TableOfContentsGrid items={mediaGuideTOC} />;
}

export function ScreensGuideTOC() {
  return <TableOfContentsGrid items={screensGuideTOC} />;
}

export function PublicationsGuideTOC() {
  return <TableOfContentsGrid items={publicationsGuideTOC} />;
}

export function UserGuideTOC() {
  return <TableOfContentsGrid items={userGuideTOC} />;
}
